<template>
    <div>
        <v-autocomplete class="mr-2" single-line
                        hide-details
                        hide-no-data
                        hide-selected
                        prepend-inner-icon="mdi-magnify"
                        label=""
                        :items="sortedItems"
                        no-filter
                        item-value="key"
                        @keydown="searchKeyDown"
                        :loading="isLoading"
                        :search-input.sync="search">
            <template slot="item" slot-scope="{item}">
                <v-list-item link :to="item.link">
                    <v-list-item-icon>
                        <v-icon :color="item.color">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title v-html="item.title"></v-list-item-title>
                        <v-list-item-subtitle v-html="item.subtitle"></v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </template>
        </v-autocomplete>
    </div>
</template>

<script>
import CreditInvoice from "@/models/Proposal/Invoice/CreditInvoice";
import DebitInvoice from "@/models/Proposal/Invoice/DebitInvoice";
import Customer from "@/models/Customer/Customer";
import Dossier from "@/models/Dossier/Dossier";
import {debounce, sortBy} from "lodash";
import Ticket from "@/models/Dossier/Ticket/Ticket";

export default {
    data() {
        return {
            isLoading: false,
            search: null,
            apiResponses: [],
            items: [],
            term: null,
        }
    },
    watch: {
        search: {
            handler: debounce(function (term){
                this.searchTerm(term)
            },300)

        }
    },
    computed: {
        sortedItems() {
            return sortBy(this.items, 'rank');
        }
    },
    methods: {
        clearList(){
            this.items = [];
        },
        searchTerm(term){
            if (!term) {
                return Promise.resolve();
            }

            this.term = term.trim();

            this.items = [];
            this.isLoading = true;
            let promises = [];
            term = term.toLowerCase();
            const termHasLetters = term.match(/[a-z]+/g) !== null;
            const termIsNumber = /^\d+$/.test(this.term);

            if (term.length < 3) {
                this.isLoading = false;
                return Promise.resolve();
            }

            if (term.startsWith('fb') || term.startsWith('tb')) {
                promises.push(this.getDebitInvoices(term));
                promises.push(this.getCreditInvoices(term));
                return Promise.all(promises).then(() => {
                    this.isLoading = false;
                });
            }

            if (termHasLetters) {
                promises.push(this.getCustomers(term));
            }

            if (termIsNumber) {
                promises.push(this.getDossiers(term));
            }

            promises.push(this.getTickets(term));

            return Promise.all(promises).then(() => {
                this.isLoading = false;
            });
        },
        searchKeyDown(event) {
            const eventValue = event.target.value;
            const isNumber = /^\d+$/.test(eventValue);

            if (event.keyCode === 13) {
                event.preventDefault();

                const goToRoute = '/dossier/' + eventValue + '/communicatie';
                if (isNumber && this.$route.path !== goToRoute) {
                    this.$router.push(goToRoute);
                    this.clearList();
                }
            }
        },
        async getDebitInvoices(term) {
            return CreditInvoice.api().where('invoice_number', term).with(['proposal']).limit(5).get().then(invoices => {
                invoices.forEach(invoice => {
                    this.items.push({
                        rank: 5,
                        key: 'credit_' + invoice.id,
                        title: 'Credit ' + invoice.invoice_number,
                        subtitle: 'dossier: ' + invoice.proposal.dossier_id + ' | prijs: &euro;&nbsp;' + this.$options.filters.roundedTwoDecimal(invoice.total_price),
                        icon: 'mdi-receipt',
                        color: 'red',
                        link:  '/proposal/' + invoice.proposal_id + '/financieel',
                    });
                });
            });
        },
        async getCreditInvoices(term) {
            return DebitInvoice.api().where('invoice_number', term).with(['proposal']).limit(5).get().then(invoices => {
                invoices.forEach(invoice => {
                    this.items.push({
                        rank: 6,
                        key: 'debit_' + invoice.id,
                        title: 'Debit ' + invoice.invoice_number,
                        subtitle: 'dossier: ' + invoice.proposal.dossier_id + ' | prijs: &euro;&nbsp;' + this.$options.filters.roundedTwoDecimal(invoice.total_price),
                        icon: 'mdi-receipt',
                        color: 'red',
                        link:  '/proposal/' + invoice.proposal_id + '/financieel',
                    });
                });
            })
        },
        async getCustomers(term) {
            return Customer.api()
                .where('term', term)
                .appended(['active_dossiers_count'])
                .orderBy('active_dossiers_count', 'desc')
                .orderBy('term_relevance','desc')
                .limit(5)
                .get()
                .then(customers => {
                    customers.forEach(customer => {
                        this.items.push({
                            rank: 2,
                            key: 'customer_' + customer.id,
                            title: customer.firstname + ' ' + customer.lastname,
                            subtitle: customer.active_dossiers_count + ' actieve dossiers',
                            icon: 'mdi-account-group',
                            color: 'green',
                            link: '/customer/' + customer.id,
                        });
                    });
                });
        },
        async getDossiers(term) {
            return Dossier.api()
                .with(['customer'])
                .where('id', term)
                .orderBy('trip_starts_at', 'desc')
                .limit(5)
                .get()
                .then(dossiers => {
                    dossiers.forEach(dossier => {
                        this.items.push({
                            rank: 1,
                            key: 'dossier_' + dossier.id,
                            title: dossier.id,
                            subtitle: dossier.customer.firstname + ' ' + dossier.customer.lastname + ' | vertrek: ' + this.$options.filters.formattedDateTime(dossier.trip_starts_at),
                            icon: 'mdi-view-list',
                            color: 'primary',
                            link: '/dossier/' + dossier.id + '/communicatie',
                        });
                    });
                });
        },
        async getTickets(term) {
            return Ticket.api()
                .where('term', term)
                .with(['traveller.version'])
                .limit(5)
                .get()
                .then(tickets => {
                    tickets.forEach(ticket => {
                        this.items.push({
                            rank: 4,
                            key: 'ticket_' + ticket.id,
                            title: ticket.traveller ? ticket.traveller.version.firstname + ' ' + ticket.traveller.version.lastname : ticket.traveller_name,
                            subtitle: 'dossier: ' + (ticket.traveller ? ticket.traveller.dossier_id : '???'),
                            icon: 'mdi-airplane',
                            color: 'deep-purple',
                            link: '/dossier/' + (ticket.traveller ? ticket.traveller.dossier_id : '#'),
                        });
                    });
                });
        }
    }
}
</script>
